// ** React Imports
import { useState, useEffect, ElementType, ChangeEvent } from 'react'

// ** MUI Imports
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Card from '@mui/material/Card'
import { styled } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import Button, { ButtonProps } from '@mui/material/Button'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Divider from '@mui/material/Divider'

// ** Third Party Imports
import { useFormik } from 'formik'
import * as Yup from 'yup'
import axios from 'src/services/ApiService'

// ** Initial Form State
const initialFormState = {
    firstName: '',
    lastName: '',
    email: '',
    imgSrc: '/images/avatars/1.png',
}

// ** Form validation
const validationSchema = Yup.object({
    firstName: Yup.string()
        .required('First Name is Required'),
    lastName: Yup.string()
        .required('Last Name is Required'),
    email: Yup.string()
        .email('Please enter a valid email address')
        .required('Email is Required'),
})

const TabAccount = () => {
    const [imgSrc, setImgSrc] = useState<string>(initialFormState.imgSrc)
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
    const [successOpen, setSuccessOpen] = useState(false);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await axios.get('userprofile/GetCurrentUserProfile');
                const { firstName, lastName, email, imgSrc } = response.data.result;
                formik.setValues({ firstName, lastName, email, imgSrc });
                setImgSrc(imgSrc);
            } catch (error) {
                console.error('Failed to fetch user profile: ', error);
            }
        };

        fetchProfile();
    }, []);


    const formik = useFormik({
        initialValues: initialFormState,
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setIsSubmitting(true)
            try {
                await axios.post('userprofile/SaveCurrentUserProfile', { ...values, imgSrc })
                setSuccessOpen(true);  // Open success dialog
            } catch (error) {
                console.error('Failed to submit form: ', error)
            }
            setIsSubmitting(false)
        },
    })

    const handleInputImageChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (file) {
            const reader = new FileReader()
            reader.onloadend = () => {
                setImgSrc(String(reader.result))
            }
            reader.readAsDataURL(file)
        }
    }


    return (
        <Box>
            <Card>
                <form onSubmit={formik.handleSubmit}>
                    <CardHeader title='Account Details' />
                    <CardContent sx={{ pt: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <img src={imgSrc} alt='Profile Pic' style={{ width: '120px', height: '120px', borderRadius: '50%' }} />
                            <div>
                                <Button component='label' variant='contained' htmlFor='account-settings-upload-image' sx={{ marginLeft : "10px" }}>
                                    Upload New Photo
                                    <input
                                        hidden
                                        type='file'
                                        accept='image/png, image/jpeg'
                                        onChange={handleInputImageChange}
                                        id='account-settings-upload-image'
                                    />
                                </Button>
                                <Typography sx={{ mt: 5, color: 'text.disabled' }}>Allowed PNG or JPEG.</Typography>
                            </div>
                        </Box>
                    </CardContent>
                    <Divider />
                    <CardContent>
                        <Grid container spacing={6}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label='First Name'
                                    name='firstName'
                                    onChange={formik.handleChange}
                                    value={formik.values.firstName}
                                    error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                                    helperText={formik.touched.firstName && formik.errors.firstName}
                                    variant='outlined'
                                    size='small'
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label='Last Name'
                                    name='lastName'
                                    onChange={formik.handleChange}
                                    value={formik.values.lastName}
                                    error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                                    helperText={formik.touched.lastName && formik.errors.lastName}
                                    variant='outlined'
                                    size='small'
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label='Email'
                                    name='email'
                                    onChange={formik.handleChange}
                                    value={formik.values.email}
                                    error={formik.touched.email && Boolean(formik.errors.email)}
                                    helperText={formik.touched.email && formik.errors.email}
                                    variant='outlined'
                                    size='small'
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button variant='contained' sx={{ mr: 3 }} type='submit' disabled={isSubmitting}>
                                    {isSubmitting ? <CircularProgress size={24} /> : 'Save Changes'}
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </form>
            </Card>
            <Dialog open={successOpen} onClose={() => setSuccessOpen(false)} aria-labelledby="form-success-dialog">
                <DialogTitle id="form-success-dialog">Success</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Profile Updated successfully!
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSuccessOpen(false)} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default TabAccount
